/**
 * NOS-262: Shared scaffold for the media taxonomy manager pages
 * (topics / speakers / series): header + table + create/edit dialog +
 * delete confirmation, wired to an org-taxonomy hook. Per-type columns
 * and form fields come in via props; mutation errors surface in toasts.
 */
import { useState, type Dispatch, type ReactNode, type SetStateAction } from "react"
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react"
import { Navigate, Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { MediaSectionNav } from "@/components/dashboard/MediaSectionNav"
import { PermissionGate } from "@/components/PermissionGate"
import { useOrgStore } from "@/stores/orgStore"
import { supabase } from "@/lib/supabase"
import { toast } from "sonner"
import type { TaxonomyResult } from "@/hooks/useOrgTaxonomy"

export interface TaxonomyColumn<Row> {
  header?: ReactNode
  headClassName?: string
  cell: (row: Row) => ReactNode
  cellClassName?: string
}

interface TaxonomyState<Row, Input> {
  rows: Row[]
  loading: boolean
  error: string | null
  refresh: () => void | Promise<void>
  create: (values: Input) => Promise<TaxonomyResult<Row>>
  update: (id: string, values: Input) => Promise<TaxonomyResult<Row>>
  remove: (id: string) => Promise<TaxonomyResult<Row>>
}

export interface MediaTaxonomyPageProps<Row extends { id: string }, Form, Input> {
  useTaxonomy: (orgId: string | null) => TaxonomyState<Row, Input>
  title: string
  tagline: string
  /** Capitalized singular ("Topic") — used in toasts and dialog titles. */
  entity: string
  columns: TaxonomyColumn<Row>[]
  emptyForm: Form
  formFromRow: (row: Row) => Form
  /** Returns a toast message when the form is invalid, null when saveable. */
  validate: (form: Form) => string | null
  toPayload: (form: Form) => Input
  renderFields: (
    form: Form,
    setForm: Dispatch<SetStateAction<Form>>,
    editing: Row | null
  ) => ReactNode
  deleteWarning: string
  dialogClassName?: string
}

export function MediaTaxonomyPage<Row extends { id: string }, Form, Input>({
  useTaxonomy,
  title,
  tagline,
  entity,
  columns,
  emptyForm,
  formFromRow,
  validate,
  toPayload,
  renderFields,
  deleteWarning,
  dialogClassName,
}: MediaTaxonomyPageProps<Row, Form, Input>) {
  const { currentOrg } = useOrgStore()
  const orgId = currentOrg?.id ?? null
  const { rows, loading, error, refresh, create, update, remove } = useTaxonomy(orgId)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Row | null>(null)
  const [form, setForm] = useState<Form>(emptyForm)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const entityLower = entity.toLowerCase()

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setDialogOpen(true)
  }

  const openEdit = (row: Row) => {
    setEditing(row)
    setForm(formFromRow(row))
    setDialogOpen(true)
  }

  const handleSave = async () => {
    const invalid = validate(form)
    if (invalid) {
      toast.error(invalid)
      return
    }
    const r = editing
      ? await update(editing.id, toPayload(form))
      : await create(toPayload(form))
    if (r.ok) {
      toast.success(editing ? `${entity} updated` : `${entity} created`)
      setDialogOpen(false)
      refresh()
    } else {
      toast.error(r.error ?? (editing ? "Update failed" : "Create failed"))
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    const r = await remove(deleteId)
    setDeleting(false)
    setDeleteId(null)
    if (r.ok) {
      toast.success(`${entity} deleted`)
      refresh()
    } else {
      toast.error(r.error ?? "Delete failed")
    }
  }

  return (
    <PermissionGate permission="can_manage_media" fallback={<Navigate to="/dashboard" replace />}>
      <div className="min-w-0 space-y-6">
        <div>
          <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2">
            <Link to="/dashboard/media">← Videos</Link>
          </Button>
          <MediaSectionNav />
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold">{title}</h1>
              <p className="text-muted-foreground">{tagline}</p>
            </div>
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              New {entityLower}
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((c, i) => (
                    <TableHead key={i} className={c.headClassName}>
                      {c.header}
                    </TableHead>
                  ))}
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    {columns.map((c, i) => (
                      <TableCell key={i} className={c.cellClassName}>
                        {c.cell(row)}
                      </TableCell>
                    ))}
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => setDeleteId(row.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={dialogClassName}>
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${entityLower}` : `New ${entityLower}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">{renderFields(form, setForm, editing)}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {entityLower}?</AlertDialogTitle>
            <AlertDialogDescription>{deleteWarning}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PermissionGate>
  )
}

/**
 * Image upload slot for the speaker-avatar / series-cover fields: uploads to
 * the org's folder in the "media" storage bucket and reports the public URL.
 */
export function MediaImageUploadField({
  folder,
  successMessage,
  onUploaded,
}: {
  folder: string
  successMessage: string
  onUploaded: (url: string) => void
}) {
  const { currentOrg } = useOrgStore()
  const orgId = currentOrg?.id ?? null
  const [uploading, setUploading] = useState(false)

  const handleFile = async (file: File | null) => {
    if (!file || !orgId) return
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image")
      return
    }
    setUploading(true)
    const path = `${orgId}/media/${folder}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`
    const { error } = await supabase.storage.from("media").upload(path, file, { upsert: true })
    setUploading(false)
    if (error) {
      toast.error(error.message)
      return
    }
    const { data } = supabase.storage.from("media").getPublicUrl(path)
    onUploaded(data.publicUrl)
    toast.success(successMessage)
  }

  return (
    <Input
      type="file"
      accept="image/*"
      disabled={uploading}
      onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
    />
  )
}
